import {
  Paragraph, TextRun, Table, TableRow, TableCell,
  WidthType, BorderStyle, ShadingType, AlignmentType,
} from 'docx';

const NONE = { style: BorderStyle.NONE, size: 0, color: 'FFFFFF' };
const NO_BORDERS = { top: NONE, bottom: NONE, left: NONE, right: NONE };
const TABLE_NO_BORDERS = { ...NO_BORDERS, insideH: NONE, insideV: NONE };

function subheading(text) {
  return new Paragraph({
    children: [new TextRun({ text, bold: true, size: 24 })],
    border: { bottom: { style: BorderStyle.SINGLE, size: 6, color: '198754' } },
    spacing: { before: 200, after: 100 },
  });
}

function twoColTable(rows) {
  return new Table({
    rows: rows.map(([label, value], i) =>
      new TableRow({
        children: [
          new TableCell({
            children: [new Paragraph({ children: [new TextRun({ text: label, size: 20 })], spacing: { after: 40 } })],
            width: { size: 55, type: WidthType.PERCENTAGE },
            shading: { fill: i % 2 === 0 ? 'f8f9fa' : 'FFFFFF', type: ShadingType.SOLID },
            borders: NO_BORDERS,
          }),
          new TableCell({
            children: [new Paragraph({ children: [new TextRun({ text: String(value ?? '–'), size: 20 })], spacing: { after: 40 } })],
            width: { size: 45, type: WidthType.PERCENTAGE },
            shading: { fill: i % 2 === 0 ? 'f8f9fa' : 'FFFFFF', type: ShadingType.SOLID },
            borders: NO_BORDERS,
          }),
        ],
      })
    ),
    width: { size: 100, type: WidthType.PERCENTAGE },
    borders: TABLE_NO_BORDERS,
  });
}

export function renderYmparistoBlockDocx({ block, children, rakennusData }) {
  const buildings = rakennusData?.rakennukset_fulls || [];

  if (!buildings.length) {
    children.push(new Paragraph({ children: [new TextRun({ text: 'Ei ympäristötietoja saatavilla.', size: 22 })], spacing: { after: 120 } }));
    return;
  }

  // ── Pohjavesialueet ─────────────────────────────────────────────────────────
  children.push(subheading('Pohjavesialueet'));

  const allPohjavesi = buildings.flatMap(r => r.aluetiedot?.pohjavesi || []);
  const uniquePohjavesi = [...new Map(allPohjavesi.map(p => [`${p.nimi}|${p.luokka}`, p])).values()];

  if (!uniquePohjavesi.length) {
    children.push(new Paragraph({ children: [new TextRun({ text: 'Kohde ei sijaitse pohjavesialueella.', size: 22 })], spacing: { after: 80 } }));
  } else {
    uniquePohjavesi.forEach(p => {
      const parts = [p.nimi, p.luokka && `(${p.luokka})`, p.etäisyys && `– ${p.etäisyys}`].filter(Boolean).join(' ');
      children.push(new Paragraph({ children: [new TextRun({ text: parts, size: 22 })], bullet: { level: 0 }, spacing: { after: 60 } }));
    });
  }

  // ── Tulvariskialueet ────────────────────────────────────────────────────────
  children.push(subheading('Tulvariskialueet'));

  const allTulva = buildings.flatMap(r => r.aluetiedot?.tulvariski || []);
  const uniqueTulva = [...new Map(allTulva.map(t => [`${t.lähde}|${t.nimi}`, t])).values()];

  if (!uniqueTulva.length) {
    children.push(new Paragraph({ children: [new TextRun({ text: 'Kohde ei sijaitse tulvariskialueella.', size: 22 })], spacing: { after: 80 } }));
  } else {
    uniqueTulva.forEach(t => {
      const text = [t.lähde && `${t.lähde} –`, t.nimi].filter(Boolean).join(' ');
      children.push(new Paragraph({ children: [new TextRun({ text, size: 22 })], bullet: { level: 0 }, spacing: { after: 60 } }));
    });
  }

  // ── Radon ───────────────────────────────────────────────────────────────────
  children.push(subheading('Radon'));

  const radon = buildings[0]?.aluetiedot?.radon || null;
  const fb = buildings[0] || {};
  const postinumero = radon?.postinumero ?? fb.postinumero ?? '–';
  const alue = radon?.postitoimipaikka ?? fb.toimipaikka ?? '–';

  if (radon) {
    children.push(twoColTable([
      ['Postinumero', postinumero],
      ['Alue', alue],
      ['Mitattuja pientaloasuntoja', radon.mitattujaAS ?? '–'],
      ['Keskimääräinen radon (Bq/m³)', radon.keskiarvo ?? '–'],
      ['Mediaani (Bq/m³)', radon.mediaani ?? '–'],
      ['200 Bq/m³ ylitykset (%)', radon.yli200 ?? '–'],
      ['300 Bq/m³ ylitykset (%)', radon.yli300 ?? '–'],
      ['1000 Bq/m³ ylitykset (%)', radon.yli1000 ?? '–'],
    ]));
  } else {
    children.push(
      new Paragraph({ children: [new TextRun({ text: 'Ei mitattuja radon arvoja alueella.', size: 22 })], spacing: { after: 80 } }),
      twoColTable([['Postinumero', postinumero], ['Alue', alue]])
    );
  }

  children.push(new Paragraph({ children: [new TextRun('')], spacing: { after: 120 } }));
}
